/**
 * QUOTATION MAPPERS — conversione tra tipi UI (union string) e DTO REST (int32).
 *
 * Pattern di riferimento: `taskMappers.ts` dello stesso core. Tipi UI in string
 * union per leggibilità, backend che espone int32, conversione esclusivamente
 * ai boundary (dentro `quotationApi.ts` e nei mock handler), fallback soft con
 * `console.warn` centralizzato in `enumFromDto`.
 *
 * Fonte di verità dei types Quotation: `Quotation`, `QuotationStatus`,
 * `QuotationDto`, `QuotationInput` vivono qui. `quotationTypes.ts` li
 * re-esporta e aggiunge i tipi UI-centrici (stato dello slice, payload
 * create/update, costante `QUOTATION_STATUSES` con le chiavi i18n).
 *
 * ## Cosa NON c'è qui
 *
 * `projectName`, `location`, `referrer` e `type` non sono campi del preventivo
 * core: sono campi di estensione, serviti dagli endpoint separati del plugin
 * che li dichiara (TS-87). Il campo core omologo di `projectName` è `title`.
 */

import { enumFromDto, pickAuditFields, type AuditFields } from '@crm/shared';
import { vocabularyKeyFromValue, vocabularyValueFromKey } from '../../plugin/vocabularyRegistry';

// ─── Types UI (fonte di verità per gli enum del preventivo) ────────────
//
// SYNC CROSS-LAYER — vedi il javadoc dell'enum Java:
//   QuotationStatus -> modules/crm-util/.../constants/QuotationStatus.java
//                      + quotationTypes.ts (QUOTATION_STATUSES)
//                      + locales/{it,en}.json (quotation.statuses.*)

/** Gli stati del PRODOTTO. Chiuso di proposito: è la chiave della tabella di
 *  conversione, che deve avere una voce per ciascuno. */
export type CoreQuotationStatus =
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'cancelled';

/**
 * Lo stato come lo vede la UI: quelli del prodotto più quelli che i plugin
 * abilitati contribuiscono al vocabolario, via `QuotationStatusContributor`
 * lato backend e `vocabularyEntries` nel manifest lato client.
 *
 * L'unione è APERTA per la stessa ragione di `QuotationFileType`: chiuderla
 * vorrebbe dire che il frontend può rappresentare solo gli stati che conosceva
 * a compile-time, cioè rinunciare all'estendibilità proprio nel punto in cui
 * serve.
 */
export type QuotationStatus = CoreQuotationStatus | (string & {});

// ─── Tabelle di mapping ────────────────────────────────────────────────
// SYNC with QuotationStatus.java: DRAFT=100, SENT=200, ACCEPTED=300, REJECTED=400,
//                                 EXPIRED=500, CANCELLED=600
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/QuotationStatus.java
//
// Fascia 0-999 al core, una fascia da mille per ciascun plugin da 1000 in su, e passo 100
// dentro ogni fascia. Il perché sta nel javadoc dell'enum: il valore è persistito in colonna,
// e un numero scelto due volte da due plugin diversi produrrebbe due significati per lo stesso
// dato senza nulla che permetta di distinguerli dopo.
const QUOTATION_STATUS_TO_INT: Record<CoreQuotationStatus, number> = {
  draft: 100,
  sent: 200,
  accepted: 300,
  rejected: 400,
  expired: 500,
  cancelled: 600,
};
const QUOTATION_STATUS_FROM_INT: Record<number, CoreQuotationStatus> =
  invert(QUOTATION_STATUS_TO_INT);

// ─── API di mapping singolo enum ───────────────────────────────────────

/**
 * Al DTO: prima gli stati del prodotto, poi quelli contribuiti dai plugin.
 *
 * Uno stato sconosciuto a entrambi non è recuperabile — mandare zero
 * scriverebbe in colonna un valore che non significa niente — quindi si
 * solleva. Può capitare solo scrivendo a mano una stringa che nessuno dichiara,
 * cioè per un errore di battitura, ed è bene che si veda subito.
 */
const quotationStatusToDto = (v: QuotationStatus): number => {
  const core = QUOTATION_STATUS_TO_INT[v as CoreQuotationStatus];
  if (core !== undefined) return core;

  const contributed = vocabularyValueFromKey('quotationStatus', v);
  if (contributed !== undefined) return contributed;

  throw new Error(
    `[quotationStatus] "${v}" non è uno stato del prodotto né uno stato ` +
    'contribuito da un plugin abilitato.',
  );
};

/**
 * Dal DTO: prima gli stati del prodotto, poi il vocabolario contribuito dai
 * plugin, e solo alla fine il ripiego.
 *
 * L'ordine è il punto, ed è lo stesso di `quotationFileTypeFromDto`. Senza il
 * controllo di mezzo, uno stato contribuito cadeva su `'draft'` — e poiché il
 * verso opposto avrebbe poi riscritto 100, un salvataggio qualunque avrebbe
 * **cancellato dalla colonna** lo stato del plugin, senza errore e senza che
 * nessun vincolo lo segnalasse.
 *
 * Il ripiego resta per i valori che nessuno dichiara — dati vecchi o un plugin
 * spento — e lì avvisare in console e mostrare qualcosa è meglio che rompere la
 * pagina. Il chip prende il colore di ripiego, che in `_components.scss` è già
 * previsto per gli stati fuori elenco.
 */
const quotationStatusFromDto = (v: number): QuotationStatus => {
  const core = QUOTATION_STATUS_FROM_INT[v];
  if (core !== undefined) return core;

  const contributed = vocabularyKeyFromValue('quotationStatus', v);
  if (contributed !== undefined) return contributed;

  return enumFromDto('QuotationStatus', v, QUOTATION_STATUS_FROM_INT, 'draft');
};

/**
 * Vero se il valore è uno stato ammesso: del prodotto o contribuito da un
 * plugin abilitato. Serve ai mock per rifiutare uno stato inesistente come fa
 * il backend, senza che debbano ricopiarsi la tabella — e quindi senza che
 * possano divergere da essa.
 *
 * È l'omologo client di `QuotationVocabularyManager.isValidStatus(groupId,
 * status)`, e come quello **non** si limita al vocabolario core: uno stato
 * contribuito è legittimo tanto quanto uno di prodotto.
 */
export function isValidQuotationStatus(value: number): boolean {
  return (
    value in QUOTATION_STATUS_FROM_INT ||
    vocabularyKeyFromValue('quotationStatus', value) !== undefined
  );
}

// ─── DTO completo del Quotation (shape REST, int al posto degli enum) ──

/**
 * Shape del Quotation come esce dal REST (`/o/crm/v1_0/quotation/{id}` e
 * annidato in `/quotations/{groupId}`). Rispecchia lo schema `Quotation` del
 * `rest-openapi.yaml` di crm-rest-impl. Gli enum sono int32.
 *
 * `businessName` NON è presente nel DTO: è un campo UI denormalizzato che il
 * client risolve via lookup sul Business (vedi `businessApi`).
 */
export interface QuotationDto {
  quotationId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  externalCode: string | null;
  businessId: number;
  title: string;
  dueDate: string | null;
  amount: number;
  status: number;
  notes: string | null;
  /** FK allo stato attuale della pipeline (0 = nessuno). Vedi entità Pipeline (TS-93). */
  currentStateId: number;
}

// ─── Shape UI del Quotation (con enum come union string) ───────────────

export interface Quotation extends AuditFields {
  quotationId: number;
  /** Codice esterno, univoco nel sito quando valorizzato. Il backend normalizza il vuoto a null. */
  externalCode: string | null;
  businessId: number;
  /**
   * Denormalizzato UI-only: non presente nel DTO wire. Risolto dal client via
   * `businessApi.getById` quando serve mostrarlo. Nell'inline edit di
   * `businessId` può essere pre-valorizzato per avere consistenza immediata
   * senza attendere un refetch.
   */
  businessName?: string;
  title: string;
  dueDate: string | null;
  /**
   * Importo totale. Il backend lo **ricalcola** dalla somma delle righe quando
   * almeno una ha importo valorizzato; solo se nessuna riga ce l'ha conserva il
   * valore inserito a mano. Lato UI va quindi trattato come scrivibile ma non
   * necessariamente conservato: dopo un update conta il valore che torna nella
   * response, non quello inviato.
   */
  amount: number;
  status: QuotationStatus;
  notes: string | null;
  /**
   * FK allo stato attuale della pipeline (0 = nessuno). Popolato dal backend; in
   * TS-93 non c'è ancora una UI di assegnazione preventivo->pipeline, quindi
   * resta 0 finché un ticket successivo non introdurrà lo spostamento fra stati.
   */
  currentStateId: number;
}

// ─── DTO di input (POST /quotations/{groupId} e PATCH /quotation/{id}) ─

/**
 * Shape del payload accettato dal backend in create e update. Allineato a
 * `QuotationInput` del `rest-openapi.yaml`: solo campi business, esclude gli
 * audit (server-side) e `quotationId` (sta nel path). Gli enum viaggiano come
 * int32.
 */
export interface QuotationInput {
  externalCode?: string | null;
  businessId?: number;
  title?: string;
  dueDate?: string | null;
  amount?: number;
  /**
   * In creazione il backend lo ignora — il preventivo nasce sempre in bozza.
   * In PATCH è la via per le transizioni di stato: il resource lo instrada su
   * `updateStatus`, non esiste un endpoint dedicato come per Task.
   */
  status?: number;
  notes?: string | null;
  /**
   * Stato attuale della pipeline. Canale dedicato lato server (come lo status);
   * in creazione viene ignorato. Inviato solo in update, 0 azzera l'assegnazione.
   */
  currentStateId?: number;
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

/**
 * DTO REST -> modello UI. Usato su GET e sulle response di POST/PATCH.
 * `businessName` resta `undefined`: il client lo risolve se e quando serve.
 */
export function quotationFromDto(dto: QuotationDto): Quotation {
  return {
    quotationId: dto.quotationId,
    ...pickAuditFields(dto),
    externalCode: dto.externalCode,
    businessId: dto.businessId,
    title: dto.title,
    dueDate: dto.dueDate,
    amount: dto.amount,
    status: quotationStatusFromDto(dto.status),
    notes: dto.notes,
    currentStateId: dto.currentStateId,
  };
}

/**
 * Modello UI -> DTO REST. Serve ai mock MSW per riprodurre esattamente la shape
 * che emette il backend (enum come int32). L'app reale non lo chiama: chi invia
 * un preventivo passa da `quotationCreateToInput` / `quotationUpdateToInput`.
 */
export function quotationToDto(q: Quotation): QuotationDto {
  return {
    quotationId: q.quotationId,
    ...pickAuditFields(q),
    externalCode: q.externalCode,
    businessId: q.businessId,
    title: q.title,
    dueDate: q.dueDate,
    amount: q.amount,
    status: quotationStatusToDto(q.status),
    notes: q.notes,
    currentStateId: q.currentStateId,
  };
}

/**
 * DTO input (body di PATCH) -> `Partial<Quotation>` UI. Usato dai mock MSW per
 * applicare la patch allo storage, che tiene la shape UI.
 */
export function quotationInputFromDto(input: QuotationInput): Partial<Quotation> {
  const out: Partial<Quotation> = {};
  if (input.externalCode !== undefined) out.externalCode = input.externalCode;
  if (input.businessId !== undefined) out.businessId = input.businessId;
  if (input.title !== undefined) out.title = input.title;
  if (input.dueDate !== undefined) out.dueDate = input.dueDate;
  if (input.amount !== undefined) out.amount = input.amount;
  if (input.status !== undefined) out.status = quotationStatusFromDto(input.status);
  if (input.notes !== undefined) out.notes = input.notes;
  if (input.currentStateId !== undefined) out.currentStateId = input.currentStateId;
  return out;
}

/**
 * Payload create UI -> DTO input. `status` non viene inviato: il backend
 * inizializza a DRAFT in creazione e ignorerebbe comunque il valore.
 *
 * `currentStateId` invece SÌ (TS-101): è lo stato di pipeline scelto in
 * creazione dalla combo a cascata. Obbligatorio solo se esiste ≥1 pipeline —
 * il controllo è nel resource backend; qui si invia il valore (0 = nessuno).
 */
export function quotationCreateToInput(p: {
  externalCode?: string | null;
  businessId: number;
  title: string;
  dueDate?: string | null;
  amount?: number;
  notes?: string | null;
  currentStateId?: number;
}): QuotationInput {
  return {
    externalCode: p.externalCode ?? null,
    businessId: p.businessId,
    title: p.title,
    dueDate: p.dueDate ?? null,
    amount: p.amount ?? 0,
    notes: p.notes ?? null,
    currentStateId: p.currentStateId ?? 0,
  };
}

/**
 * Payload update UI -> DTO input parziale (PATCH). Include solo i campi
 * presenti nel payload: gli assenti mantengono il valore corrente.
 *
 * `externalCode: null` è semanticamente diverso da `externalCode` assente e la
 * distinzione viene rispettata: il null viene inviato e azzera il campo.
 *
 * **Eccezione nota su `dueDate`**: il backend fonde il campo con
 * `GetterUtil.getDate(...)`, che tratta il null come "non fornito". Inviare
 * `dueDate: null` quindi **non svuota** la scadenza, la lascia com'è. Per
 * azzerarla oggi non esiste una via dal client — è un limite del merge
 * server-side, non di questo mapper.
 */
export function quotationUpdateToInput(p: {
  externalCode?: string | null;
  businessId?: number;
  title?: string;
  dueDate?: string | null;
  amount?: number;
  status?: QuotationStatus;
  notes?: string | null;
  currentStateId?: number;
}): QuotationInput {
  const out: QuotationInput = {};
  if (p.externalCode !== undefined) out.externalCode = p.externalCode;
  if (p.businessId !== undefined) out.businessId = p.businessId;
  if (p.title !== undefined) out.title = p.title;
  if (p.dueDate !== undefined) out.dueDate = p.dueDate;
  if (p.amount !== undefined) out.amount = p.amount;
  if (p.status !== undefined) out.status = quotationStatusToDto(p.status);
  if (p.notes !== undefined) out.notes = p.notes;
  if (p.currentStateId !== undefined) out.currentStateId = p.currentStateId;
  return out;
}

// ─── Helper ────────────────────────────────────────────────────────────

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
